import React, { useMemo, useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import ContactForm from '../components/ContactForm';
import {
  FaFileInvoiceDollar,
  FaBalanceScale,
  FaMoneyCheckAlt,
  FaCalculator,
  FaChartLine,
  FaPiggyBank,
  FaChartBar,
  FaBroom,
  FaUserTie,
  FaShieldAlt,
  FaClipboardList,
} from 'react-icons/fa';
import { BsCashCoin } from "react-icons/bs";

import bgImage from '../assets/mobapp.jpg';

/**
 * Bookkeeping & Financial Management page styled like CyberSecurityPage
 * - semantic structure, reduced-motion support, responsive typography
 */

const gradientText = {
  background: 'linear-gradient(to right, #10b981, #0ea5e9)', // emerald → sky
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

const wrapStyle = (imageUrl) => ({
  minHeight: '100vh',
  padding: '80px 20px',
  color: '#fff',
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  backgroundAttachment: 'fixed',
  backgroundImage: `linear-gradient(rgba(15,23,42,0.92), rgba(2,6,23,0.95)), url(${imageUrl})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
});

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '30px',
  maxWidth: '1200px',
  margin: '0 auto',
};

const cardStyleBase = {
  borderRadius: '16px',
  padding: '30px',
  color: '#fff',
  textAlign: 'left',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
};

const iconWrapStyle = {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.5rem',
  marginBottom: '20px',
  background: 'rgba(255, 255, 255, 0.15)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
};

const sectionShell = {
  background: 'linear-gradient(to right, rgba(30,41,59,0.8), rgba(15,23,42,0.9))',
  borderRadius: '24px',
  padding: '50px',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
  maxWidth: 'min(1000px, 90vw)',
  width: '100%',
  margin: '80px auto 0',
};

const listGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
  gap: '20px',
  maxWidth: '900px',
  margin: '0 auto',
  padding: 0,
  listStyle: 'none',
};

const Bookkeeping = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const prefersReducedMotion = useReducedMotion();

  const services = useMemo(
    () => [
      {
        icon: <FaFileInvoiceDollar aria-hidden="true" />,
        title: 'Accounts Payable & Receivable',
        description:
          'Manage invoices, payments, and collections to keep cash flow steady. On‑time vendor payments and prompt receivables reduce stress and errors.',
        color: 'linear-gradient(135deg,#10b981,#14b8a6)',
      },
      {
        icon: <FaBalanceScale aria-hidden="true" />,
        title: 'Reconciliations',
        description:
          'Regularly match statements to ensure every transaction is accurate. Prevent discrepancies from becoming costly issues and keep accounts reliable.',
        color: 'linear-gradient(135deg,#3b82f6,#0ea5e9)',
      },
      {
        icon: <FaMoneyCheckAlt aria-hidden="true" />,
        title: 'Payroll Support',
        description:
          'Simplify payroll so employees are paid accurately and on time. Maintain clear, compliant records as your team grows.',
        color: 'linear-gradient(135deg,#8b5cf6,#d946ef)',
      },
      {
        icon: <FaCalculator aria-hidden="true" />,
        title: 'Tax Tracking & Filing Support',
        description:
          'Track, prepare, and organize taxes to meet deadlines stress‑free. Reduce risk of errors and penalties with accurate records.',
        color: 'linear-gradient(135deg,#0ea5e9,#06b6d4)',
      },
      {
        icon: <FaChartLine aria-hidden="true" />,
        title: 'Financial Reporting',
        description:
          'Turn numbers into insights with clear statements and dashboards. Identify performance drivers and opportunities for growth.',
        color: 'linear-gradient(135deg,#f59e0b,#ef4444)',
      },
      {
        icon: <BsCashCoin aria-hidden="true" />,
        title: 'Cash Flow Management',
        description:
          'Monitor inflows and outflows to stay stable and predictable. Reduce surprises and support both short‑term needs and long‑term goals.',
        color: 'linear-gradient(135deg,#16a34a,#10b981)',
      },
      {
        icon: <FaChartBar aria-hidden="true" />,
        title: 'Budgeting & Forecasting',
        description:
          'Build budgets aligned to strategy and resources. Accurate forecasting improves visibility, enabling proactive decisions and risk reduction.',
        color: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
      },
      {
        icon: <FaBroom aria-hidden="true" />,
        title: 'Catch‑Up & Cleanup',
        description:
          'Restore outdated or disorganized records to accuracy. Create a fresh, compliant starting point for reliable reporting.',
        color: 'linear-gradient(135deg,#111827,#6b7280)',
      },
      {
        icon: <FaUserTie aria-hidden="true" />,
        title: 'Virtual CFO Services',
        description:
          'Strategic guidance beyond bookkeeping—analyze trends, manage risk, and plan for growth to elevate financial management.',
        color: 'linear-gradient(135deg,#0ea5e9,#3b82f6)',
      },
      {
        icon: <FaShieldAlt aria-hidden="true" />,
        title: 'Compliance & Audit Support',
        description:
          'Prepare for audits with organized, transparent records. Proactive compliance builds trust with regulators and stakeholders.',
        color: 'linear-gradient(135deg,#10b981,#22c55e)',
      },
      {
        icon: <FaClipboardList aria-hidden="true" />,
        title: 'Expense Management',
        description:
          'Simplify expense tracking and approvals. Maintain visibility and accountability to prevent overspending and boost efficiency.',
        color: 'linear-gradient(135deg,#06b6d4,#0ea5e9)',
      },
    ],
    []
  );

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: prefersReducedMotion
        ? { duration: 0 }
        : { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { y: prefersReducedMotion ? 0 : 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: prefersReducedMotion
        ? { duration: 0 }
        : { type: 'spring', stiffness: 100, damping: 10 },
    },
  };

  return (
    <main style={wrapStyle(bgImage)} ref={ref} aria-label="Bookkeeping & Financial Management Page">
      <motion.div
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        variants={containerVariants}
        style={{ maxWidth: '1280px', margin: '0 auto' }}
      >
        <header style={{ textAlign: 'center' }}>
          <motion.h1
            style={{
              ...gradientText,
              fontSize: 'clamp(2.25rem, 3vw + 1rem, 3.5rem)',
              fontWeight: 700,
              marginBottom: 24,
            }}
            variants={itemVariants}
          >
            Bookkeeping & Financial Management
          </motion.h1>

          <motion.p
            style={{
              fontSize: 'clamp(1rem, 0.5vw + 0.9rem, 1.25rem)',
              maxWidth: 900,
              margin: '0 auto 60px',
              color: '#d1d5db',
              lineHeight: 1.7,
            }}
            variants={itemVariants}
          >
            We deliver bookkeeping solutions that keep records accurate, compliant, and easy to understand—freeing you to focus on growth and strategy. With clear reporting and reliable processes, you always know where your business stands.
          </motion.p>
        </header>

        <section aria-labelledby="services-heading">
          <h2 id="services-heading" style={{ position: 'absolute', left: -10000 }}>
            Our Services
          </h2>

          <motion.div style={gridStyle} variants={containerVariants}>
            {services.map((s) => (
              <motion.article
                key={s.title}
                aria-label={s.title}
                style={{ ...cardStyleBase, background: s.color }}
                variants={itemVariants}
                whileHover={
                  prefersReducedMotion
                    ? undefined
                    : { y: -8, scale: 1.03, boxShadow: '0 15px 30px rgba(0,0,0,0.4)' }
                }
              >
                <div style={iconWrapStyle} aria-hidden="true">{s.icon}</div>
                <h3 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 15 }}>
                  {s.title}
                </h3>
                <p style={{ fontSize: '1rem', color: '#e5e7eb', lineHeight: 1.6 }}>{s.description}</p>
              </motion.article>
            ))}
          </motion.div>
        </section>

        <section aria-labelledby="why-heading">
          <motion.div style={sectionShell} variants={itemVariants}>
            <h2
              id="why-heading"
              style={{
                ...gradientText,
                fontSize: 'clamp(1.75rem, 1.5vw + 1rem, 2.5rem)',
                fontWeight: 700,
                marginBottom: 24,
                textAlign: 'center',
              }}
            >
              Why Our Finance Ops Work
            </h2>

            <p
              style={{
                fontSize: 'clamp(1rem, 0.4vw + 0.85rem, 1.2rem)',
                color: '#d1d5db',
                maxWidth: 800,
                margin: '0 auto 40px',
                lineHeight: 1.7,
                textAlign: 'center',
              }}
            >
              We combine meticulous record‑keeping with timely reporting and proactive guidance—so leaders make decisions with confidence and clarity.
            </p>

            <ul style={listGrid}>
              {[
                'Full‑fidelity records that stand up to audits',
                'Predictable cash flow through disciplined AR/AP',
                'Automations that reduce manual work and errors',
                'Dashboards that turn figures into actions',
                'Tax readiness with organized, on‑time documentation',
                'Strategic oversight from virtual CFO support',
              ].map((item, index) => (
                <motion.li
                  key={item}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: 16,
                    background: 'rgba(30,41,59,0.5)',
                    borderRadius: 12,
                    borderLeft: '4px solid #10b981',
                  }}
                  initial={{ opacity: 0, x: prefersReducedMotion ? 0 : -20 }}
                  animate={{
                    opacity: 1,
                    x: 0,
                    transition: prefersReducedMotion
                      ? { duration: 0 }
                      : { delay: 0.4 + index * 0.08 },
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-block',
                      marginRight: 12,
                      color: '#10b981',
                      fontSize: '1.5rem',
                    }}
                  >
                    •
                  </span>
                  <span style={{ color: '#e5e7eb', lineHeight: 1.6 }}>{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </section>

        <section aria-labelledby="contact-heading" style={{ marginTop: 64 }}>
          <h2 id="contact-heading" style={{ position: 'absolute', left: -10000 }}>
            Contact Us
          </h2>
          <ContactForm />
        </section>
      </motion.div>
    </main>
  );
};

export default Bookkeeping;
